import {useCallback} from "react";
import type {History, Location} from "history";
import {doneStatusSet, isCardCountable, isCardDoc} from "../../lib/card-status-utils";
import {waitForResultPromise} from "../../lib/wait-for-result";
import useMutation from "../../lib/hooks/useMutation";
import {api} from "../../lib/api";
import type {Card, CardId} from "../../cdx-models/Card";
import type {Account} from "../../cdx-models/Account";
import type {Deck} from "../../cdx-models/Deck";
import type {Root} from "../../cdx-models/Root";
import {resolveCardEffort} from "../../lib/effort-utils";
import type {WorkflowItem, WorkflowItemId} from "../../cdx-models/WorkflowItem";
import {toCardPath} from "../../layouts/arena-layout/createArenaContext";
import {passesGuardianRuleForParent} from "../../components/Deck/deck-utils";
import type {DerivedStatus} from "../../cdx-models/field-definitions";
import {isPublicContext} from "../../lib/app-mode";

export const getShownEffort = (card: Card, account: Account): number | null => {
  if (
    card.childCards.length === 0 ||
    (card.childCards.length === 1 && !card.childCards[0].$meta.isLoaded)
  ) {
    return card.effort;
  } else {
    const childEffort = calcChildCardEffort(getChildCards(card), account);

    return childEffort + (card.effort ?? 0);
  }
};

const calcChildCardEffort = (childCards: Card[], account: Account): number => {
  let sum = 0;
  childCards.forEach((c) => {
    if (!isCardCountable(c)) return;
    const eff = c.$meta.get("effort", null);
    sum += resolveCardEffort(eff, account.fallbackEffort);
  });
  return sum;
};

export const getChildCards = (card: Card): Card[] => {
  if (!card) {
    console.warn("passed card should not be empty");
    return [];
  }
  const {childCards} = card;
  if (!childCards || (childCards.length === 1 && !childCards[0].$meta.isLoaded)) return [];
  return isPublicContext ? childCards.filter((c: any) => c.isPublic) : childCards;
};

export const hasChildCards = (card: Card): boolean => {
  const childCards = getChildCards(card);
  if (childCards.length === 0) return false;
  return childCards[0].$meta.isLoaded;
};

export const getParentCard = (card: Card): Card | null => {
  if (card.parentCard === null || card.parentCard.$meta.isDeleted()) return null;
  if (isPublicContext) {
    return card.parentCard.$meta.get("isPublic", false) ? card.parentCard : null;
  }
  return card.parentCard;
};

export const getChildCardInfo = (card: Card, account: Account) => {
  const childCards = getChildCards(card);
  if (childCards.length === 0) return null;

  const childEffort = calcChildCardEffort(childCards, account);
  const count = 0;

  const info: Record<"started" | "blocked" | "review" | "done" | "rest", number[]> & {
    effort: number;
    count: number;
  } = {
    started: [],
    blocked: [],
    review: [],
    done: [],
    rest: [],
    effort: childEffort + (card.effort || 0),
    count,
  };

  const infoBuckets = new Set<string>(["started", "blocked", "review", "done"]);

  childCards.forEach((child) => {
    const effort = resolveCardEffort(child.effort, account.fallbackEffort);
    const ds = child.derivedStatus as DerivedStatus;
    if (doneStatusSet.has(ds)) {
      info.done.push(effort);
    } else if (infoBuckets.has(ds)) {
      info[ds as "started"].push(effort);
    } else if (card.isDoc && isCardDoc(child)) {
      // doc hero card: doc children count as "done"
      info.done.push(effort);
    } else if (isCardCountable(child)) {
      info.rest.push(effort);
    }
    info.count += 1;
  });

  return info;
};

export const areValidChildren = (cards: Card[]) => {
  if (cards.some((c) => !c.deck)) {
    return {isValid: false, msg: "Can't add private cards as sub cards."};
  } else if (cards.some((c) => c.childCards.length > 0)) {
    return {isValid: false, msg: "Can't add Hero cards as sub cards."};
  } else if (cards.some((c) => c.parentCard && c.parentCard.$meta.isDeleted())) {
    return {isValid: false, msg: "Card is already part of inaccessible Hero card."};
  }
  return {isValid: true};
};

export const checkDropCards = (
  parentCard: Card,
  cardInfos: {cardId: string; getCard: () => Card}[],
  {mayDropExistingChildren}: {mayDropExistingChildren?: boolean} = {}
) => {
  if (!parentCard || parentCard.$meta.isDeleted()) {
    return {canDrop: false, msg: "You don't have access to the parent card"};
  }
  if (!parentCard.deck) return {canDrop: false, msg: "Can't turn private card into a Hero card."};
  if (parentCard.isDoc) {
    const onlyAllowDocCardChildren = () => {
      if (!hasChildCards(parentCard)) return true;
      return parentCard.deck?.deckType === "doc";
    };
    if (onlyAllowDocCardChildren() && cardInfos.some((i) => !i.getCard().isDoc)) {
      return {canDrop: false, msg: "Can't add Task Card to Doc Cards."};
    }
  }
  const allCardsAreMyChildren = cardInfos.every((i) =>
    parentCard.childCards.find((c) => c.cardId === i.cardId)
  );
  if (!mayDropExistingChildren && allCardsAreMyChildren) {
    return {
      canDrop: false,
      msg: "Can't drop: already sub card of this card.",
      isAlreadyChild: true,
    };
  } else if (cardInfos.some((i) => i.cardId === parentCard.cardId)) {
    return {canDrop: false, msg: "Can't drop a card onto itself."};
  } else {
    const cards = cardInfos.map((i) => i.getCard());
    const {isValid, msg} = areValidChildren(cards);
    if (!isValid) return {canDrop: false, msg};
    const addsCard = cards.some(
      (card) => !parentCard.childCards.some((c) => c.cardId === card.cardId)
    );
    if (addsCard && !passesGuardianRuleForParent(api.getRoot(), parentCard)) {
      return {canDrop: false, msg: "Only Guardians may add Sub Cards to this finished Hero Card."};
    }
    return {canDrop: true, msg: undefined};
  }
};

export const useAddCardToParent = () => {
  const [update] = useMutation("cards", "update");
  return useCallback(
    (opts: {parentCardId: CardId; childCardIds: CardId[]}) =>
      waitForResultPromise(() => {
        const parentCard = api.getModel({modelName: "card", id: opts.parentCardId})!;
        return {
          existingIds: (parentCard.$meta.getIds("childCards", []) as CardId[]) || [],
        };
      }).then(({existingIds}) => {
        const set = new Set(existingIds);
        return update({
          id: opts.parentCardId,
          childCards: [...existingIds, ...opts.childCardIds.filter((id) => !set.has(id))],
        });
      }),
    [update]
  );
};

export const deckHasDefaultCard = (deck: Deck) => {
  if (!deck.defaultCard) return false;
  const {
    effort = null,
    priority = null,
    assigneeId = null,
    masterTags = [],
    content,
  } = deck.defaultCard as Record<string, any>;
  return (
    effort !== null ||
    priority !== null ||
    assigneeId !== null ||
    (masterTags || []).length > 0 ||
    content
  );
};

export type ChildCardInfo = {
  groups: {
    id: number;
    label: string | null;
    description: string | null;
    cards: string[];
  }[];
};

type Entry = {id: CardId; card: Card | null};
export type ChildCardEntry = Entry;

export type ChildCardInfoGroup = {
  id: number;
  label: string | null;
  description: string | null;
  entries: Entry[];
  isDefaultGroup: boolean;
};

export const getChildCardEntries = (card: Card | null, opts: {includeInaccessible: boolean}) => {
  if (!card) return [];
  const showAll = !isPublicContext && opts.includeInaccessible;
  const childCardEntries: {id: CardId; card: Card | null}[] = showAll
    ? card.childCards.map((c) => ({id: c.cardId as CardId, card: c}))
    : (card.$meta.getIds("childCards", []) || []).map((id: any) => ({
        id: id as CardId,
        card: api.getModel({id, modelName: "card"}) as Card,
      }));
  return isPublicContext ? childCardEntries.filter((c) => c.card?.isPublic) : childCardEntries;
};

export const getItemsFromDeck = (deck: Deck) =>
  deck.$meta.find("workflowItems", {visibility: "default", $order: ["sortValue", "itemId"]});

export type WorkflowPanelBag = {
  root: Root;
  history: History;
  location: Location;
  selectedIds: Set<WorkflowItemId>;
  /** The most recently selected item, used as the anchor for shift-range selection. */
  lastSelectedItemId: WorkflowItemId | null;
  addSelectedItemId: (id: WorkflowItemId) => void;
  removeSelectedItemId: (id: WorkflowItemId) => void;
  setSelection: (selection: Set<WorkflowItemId>) => void;
  selectRangeTo: (id: WorkflowItemId) => void;
  canModify: boolean | null;
  deck: Deck;
  activeItem: WorkflowItem | null;
};

export const getWorkflowItemsWithZones = (deck: Deck) => {
  const items = getItemsFromDeck(deck);
  const zones: (string | null)[] = deck.workflowItemOrderLabels;
  if (zones.length === 0) zones.push(null);
  const map = new Map<string | null, WorkflowItem[]>(zones.map((label) => [label, []]));
  const defaultZone = map.get(zones[0])!;
  for (const item of items) {
    const zone = map.get(item.label) || defaultZone;
    zone.push(item);
  }
  return {zoneMap: map, items};
};

export const workflowRoutes = {
  root: {
    path: "/:deckId",
    getUrl: (deck: Pick<Deck, "accountSeq">) => `/${deck.accountSeq}`,
  },
  createItem: {
    path: "/:deckId/step/create",
    getUrl: (deck: Pick<Deck, "accountSeq">) => `/${deck.accountSeq}/step/create`,
  },
  item: {
    path: "/:deckId/step/:itemId",
    getUrl: ({
      deck,
      item,
    }: {
      deck: Pick<Deck, "accountSeq">;
      item: Pick<WorkflowItem, "accountSeq" | "title">;
    }) => `/${deck.accountSeq}/step/${toCardPath(item)}`,
  },
  createTemplate: {
    path: "/:deckId/create-template",
    getUrl: (deck: Pick<Deck, "accountSeq">) => `/${deck.accountSeq}/create-template`,
  },
  updateTemplate: {
    path: "/:deckId/update-template",
    getUrl: (deck: Pick<Deck, "accountSeq">) => `/${deck.accountSeq}/update-template`,
  },
  templateBrowser: {
    path: "/:deckId/templates",
    getUrl: (deck: Pick<Deck, "accountSeq">) => `/${deck.accountSeq}/templates`,
  },
  templatePreview: {
    path: "/:deckId/templates/:templateId",
    getUrl: (deck: Pick<Deck, "accountSeq">, templateId: string) =>
      `/${deck.accountSeq}/templates/${templateId}`,
  },
  templateItem: {
    path: "/:deckId/templates/:templateId/step/:itemId",
    getUrl: (deck: Pick<Deck, "accountSeq">, templateId: string, itemId: string) =>
      `/${deck.accountSeq}/templates/${templateId}/step/${itemId}`,
  },
};
